import { useState, useCallback, useMemo, useEffect } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import type { FlowSection, FlowSectionField, Question, OriginalFormState } from "../types";
import { DEFAULT_SECTIONS, DEFAULT_QUESTIONS, DEFAULT_OPTION_VALUES } from "../types";
import { useFormValidation } from "./use-form-validation";
import { useChangeTracker, type TrackedChanges } from "./use-change-tracker";

interface UseFormBuilderStateOptions {
	initialFormTitle?: string;
	initialSections?: FlowSection[];
	initialQuestions?: Question[];
	initialAutoApprove?: boolean;
	initialSelectedTagIds?: string[];
	initialTheme?: "light" | "dark" | "system-default";
	initialStatus?: "published" | "unpublished";
}

export function useFormBuilderState(options: UseFormBuilderStateOptions = {}) {
	const {
		initialFormTitle = "Feedback Collection Form",
		initialSections = DEFAULT_SECTIONS,
		initialQuestions = DEFAULT_QUESTIONS,
		initialAutoApprove = false,
		initialSelectedTagIds = [],
		initialTheme = "light",
		initialStatus = "unpublished",
	} = options;

	// Core state
	const [formTitle, setFormTitle] = useState(initialFormTitle);
	const [sections, setSections] = useState<FlowSection[]>(initialSections);
	const [questions, setQuestions] = useState<Question[]>(initialQuestions);
	const [autoApproveTestimonials, setAutoApproveTestimonials] = useState(initialAutoApprove);
	const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialSelectedTagIds);
	const [theme, setTheme] = useState<"light" | "dark" | "system-default">(initialTheme);
	const [status, setStatus] = useState<"published" | "unpublished">(initialStatus);

	// UI state
	const [selectedSectionId, setSelectedSectionId] = useState<string | null>("rating");
	const [activeTab, setActiveTab] = useState<"flow" | "settings">("flow");

	// Store original values for dirty checking and discard
	const [originalState, setOriginalState] = useState<OriginalFormState>(() => ({
		formTitle: initialFormTitle,
		sections: JSON.parse(JSON.stringify(initialSections)),
		questions: JSON.parse(JSON.stringify(initialQuestions)),
		autoApproveTestimonials: initialAutoApprove,
		selectedTagIds: [...initialSelectedTagIds],
		theme: initialTheme,
		status: initialStatus,
	}));

	// Sync state when initial props change (e.g., after API data loads)
	useEffect(() => {
		setFormTitle(initialFormTitle);
		setSections(initialSections);
		setQuestions(initialQuestions);
		setAutoApproveTestimonials(initialAutoApprove);
		setSelectedTagIds(initialSelectedTagIds);
		setTheme(initialTheme);
		setStatus(initialStatus);
		setOriginalState({
			formTitle: initialFormTitle,
			sections: JSON.parse(JSON.stringify(initialSections)),
			questions: JSON.parse(JSON.stringify(initialQuestions)),
			autoApproveTestimonials: initialAutoApprove,
			selectedTagIds: [...initialSelectedTagIds],
			theme: initialTheme,
			status: initialStatus,
		});
	}, [initialFormTitle, initialSections, initialQuestions, initialAutoApprove, initialSelectedTagIds, initialTheme, initialStatus]);

	// Compute dirty state
	const hasUnsavedChanges = useMemo(() => {
		const formTitleChanged = formTitle !== originalState.formTitle;
		const sectionsChanged = JSON.stringify(sections) !== JSON.stringify(originalState.sections);
		const questionsChanged = JSON.stringify(questions) !== JSON.stringify(originalState.questions);
		const autoApproveChanged = autoApproveTestimonials !== originalState.autoApproveTestimonials;
		const tagsChanged = JSON.stringify(selectedTagIds) !== JSON.stringify(originalState.selectedTagIds);
		const themeChanged = theme !== originalState.theme;
		const statusChanged = status !== originalState.status;

		return formTitleChanged || sectionsChanged || questionsChanged || autoApproveChanged || tagsChanged || themeChanged || statusChanged;
	}, [formTitle, sections, questions, autoApproveTestimonials, selectedTagIds, theme, status, originalState]);
	// Validation
	const { validationErrors, hasValidationErrors, getFirstErrorSectionId } = useFormValidation({
		formTitle,
		sections,
		questions,
		maxQuestions: 10,
	});

	// Change tracking
	const { trackedChanges } = useChangeTracker({
		formTitle,
		sections,
		questions,
		autoApproveTestimonials,
		selectedTagIds,
		theme,
		status,
		originalState,
	});

	// Commit changes after successful save (resets original state)
	const commitChanges = useCallback(() => {
		setOriginalState({
			formTitle,
			sections: JSON.parse(JSON.stringify(sections)),
			questions: JSON.parse(JSON.stringify(questions)),
			autoApproveTestimonials,
			selectedTagIds: [...selectedTagIds],
			theme: theme,
			status,
		});
	}, [formTitle, sections, questions, autoApproveTestimonials, selectedTagIds, theme, status]);

	// Section operations
	const toggleSection = useCallback((sectionId: string) => {
		setSections((prev) => prev.map((section) => (section.id === sectionId && section.canToggle ? { ...section, enabled: !section.enabled } : section)));
	}, []);

	const updateSectionTitle = useCallback((sectionId: string, title: string) => {
		setSections((prev) => prev.map((section) => (section.id === sectionId ? { ...section, title } : section)));
	}, []);

	const updateSectionDescription = useCallback((sectionId: string, description: string) => {
		setSections((prev) => prev.map((section) => (section.id === sectionId ? { ...section, description } : section)));
	}, []);

	const updateSectionImageUrl = useCallback((sectionId: string, image_url: string | null) => {
		setSections((prev) => prev.map((section) => (section.id === sectionId ? { ...section, image_url } : section)));
	}, []);

	const updateSectionField = useCallback((sectionId: string, fieldId: string, updates: Partial<FlowSectionField>) => {
		setSections((prev) =>
			prev.map((section) => {
				if (section.id !== sectionId || !section.fields) return section;
				return {
					...section,
					fields: section.fields.map((field) => (field.id === fieldId ? { ...field, ...updates } : field)),
				};
			}),
		);
	}, []);

	// Question operations
	const addQuestion = useCallback(() => {
		const questionNumber = questions.length + 1;
		const newQuestion: Question = {
			id: `${Date.now()}`,
			title: `Question #${questionNumber}`,
			description: "",
			format: "text",
			...DEFAULT_OPTION_VALUES,
		};
		setQuestions((prev) => [...prev, newQuestion]);
		return newQuestion.id;
	}, [questions.length]);

	const removeQuestion = useCallback(
		(questionId: string) => {
			if (questions.length <= 1) return; // Prevent removing last question
			setQuestions((prev) => prev.filter((q) => q.id !== questionId));
			// Select first remaining question or rating
			const remainingQuestions = questions.filter((q) => q.id !== questionId);
			setSelectedSectionId(remainingQuestions[0]?.id || "rating");
		},
		[questions],
	);

	const updateQuestion = useCallback((questionId: string, updates: Partial<Question>) => {
		setQuestions((prev) => prev.map((q) => (q.id === questionId ? { ...q, ...updates } : q)));
	}, []);

	const reorderQuestions = useCallback((activeId: string, overId: string) => {
		setQuestions((prev) => {
			const oldIndex = prev.findIndex((q) => q.id === activeId);
			const newIndex = prev.findIndex((q) => q.id === overId);
			return arrayMove(prev, oldIndex, newIndex);
		});
	}, []);

	// Selection
	const selectSection = useCallback((sectionId: string) => {
		setSelectedSectionId(sectionId);
		// Scroll to section in preview instantly
		setTimeout(() => {
			document.getElementById(`preview-${sectionId}`)?.scrollIntoView({
				behavior: "instant",
				block: "start",
			});
		}, 50);
	}, []);

	// Discard changes
	const discardChanges = useCallback(() => {
		setFormTitle(originalState.formTitle);
		setSections(JSON.parse(JSON.stringify(originalState.sections)));
		setQuestions(JSON.parse(JSON.stringify(originalState.questions)));
		setAutoApproveTestimonials(originalState.autoApproveTestimonials);
		setSelectedTagIds([...originalState.selectedTagIds]);
		setTheme(originalState.theme);
		setStatus(originalState.status);
	}, [originalState]);

	// Get current state for saving
	const getCurrentState = useCallback(() => {
		return {
			formTitle,
			sections,
			questions,
			autoApproveTestimonials,
			selectedTagIds,
			theme,
			status,
		};
	}, [formTitle, sections, questions, autoApproveTestimonials, selectedTagIds, theme, status]);

	return {
		// State
		formTitle,
		sections,
		questions,
		autoApproveTestimonials,
		selectedTagIds,
		theme,
		status,
		selectedSectionId,
		activeTab,
		hasUnsavedChanges,

		// Validation
		validationErrors,
		hasValidationErrors,
		getFirstErrorSectionId,

		// Change tracking
		trackedChanges,

		// Setters
		setFormTitle,
		setAutoApproveTestimonials,
		setSelectedTagIds,
		setTheme,
		setStatus,
		setSelectedSectionId,
		setActiveTab,

		// Section operations
		toggleSection,
		updateSectionTitle,
		updateSectionDescription,
		updateSectionImageUrl,
		updateSectionField,

		// Question operations
		addQuestion,
		removeQuestion,
		updateQuestion,
		reorderQuestions,

		// Selection
		selectSection,

		// Actions
		discardChanges,
		getCurrentState,
		commitChanges,
	};
}

export type { TrackedChanges };
